import React, { useEffect } from 'react';
import Joyride, { CallBackProps, LIFECYCLE, STATUS, Step } from 'react-joyride';
import { JoyrideTooltip } from '@gik/ui';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { useInkind } from '@gik/api/inkinds/inkind';
import { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import { isBrowser } from '@/utils/environment';
import { openNewPageWelcomeModal } from '../NewPageWelcome/NewPageWelcome';
import { isInkindPageNew } from '@gik/inkind-page/utils/isInkindPageNew';
import { useUserStore } from '@gik/core/store/UserStore';
import { updateUserWelcomeScreenLastSeen, useUser } from '@gik/api/users/user';
import { Analytics } from '@gik/core/utils';
import userHasSeenInkindTourForThisPage from '@gik/inkind-page/utils/userHasSeenInkindTourForThisPage';
import twConfig from '@gik/core/tailwind.config';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import { hideIntercomDefaultLauncher, showIntercomDefaultLauncher } from '@gik/core/utils/Intercom';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';

export interface InkindPageTourProps {
  inkindRouteId: string;
  selectors: IInkindPageTourSelectors;
  forceOpen?: boolean;
}

// NOTE: limited to id's
export interface IInkindPageTourSelectors {
  pageSettings?: string;
  privacyPreferences?: string;
  addUpdate?: string;
  addStory?: string;
  donations?: string;
  addCalendarRequestDesktop?: string;
  addCalendarRequestMobile?: string;
  addItemsWishlist?: string;
  shareButton?: string;
  supportersButton?: string;
}

// NOTE: set selectors (ids) in a single place and import them (except mvc)
// and not hardcode them in multiple places
// use dedicated ids and not classes which might change in the future during styling
export const inkindPageTourSelectors: IInkindPageTourSelectors = {
  pageSettings: 'page-settings-page-button-anchor',
  privacyPreferences: 'privacy-preferences-page-tour-id', // icons container
  addUpdate: 'add-update-page-tour-id',
  addStory: 'add-story-page-tour-id',
  donations: 'donations-page-tour-id', // container around buttons
  addCalendarRequestDesktop: 'add-calendar-request-desktop-page-tour-id', // 767px+
  addCalendarRequestMobile: 'add-calendar-request-mobile-page-tour-id',
  addItemsWishlist: 'add-items-wishlist-page-tour-id',
  shareButton: 'share-button-page-tour-id',
  supportersButton: 'supporters-button-page-tour-id',
};

// Ensure tour can only be launched once per page load
let hasLaunchedTour = false;

export default function InkindPageTour({
  inkindRouteId,
  selectors,
  forceOpen,
}: InkindPageTourProps): React.ReactElement {
  const { t } = useTranslation();
  const isMd = useBreakpoint(Breakpoint.MD);

  const userStore = useUserStore();
  const userId = userStore.id;
  const { data: user, revalidate: revalidateUser } = useUser(userId);
  const [showTour, setShowTour] = React.useState<boolean>(false);

  const { data: inkindPage } = useInkind(inkindRouteId);

  const getSteps = (inkindPage: InkindPageAPIModel): Step[] => {
    if (!inkindPage) {
      return [];
    }

    const customSpotlightProps = {
      // styles for steps with white background
      // we need initially darker orange to get secondary[600] after mix-blend-mode: hard-light is applied
      styles: { spotlight: { borderRadius: '8px', border: '2px solid rgb(163 81 0)' } },
      spotlightPadding: 8,
    };

    const steps: Step[] = [
      {
        // always
        target: selectors.pageSettings,
        content: t(translationKeys.pageSettingsContent),
        // Page Settings uses pill button
        styles: { spotlight: { borderRadius: '9999px' } },
      },
      {
        // always
        target: selectors.privacyPreferences,
        content: t(translationKeys.privacyPreferencesContent),
        ...customSpotlightProps,
      },
      {
        // always
        target: selectors.addUpdate,
        content: t(translationKeys.updatesContent),
      },
      {
        // always, "Add Page Description" button
        target: selectors.addStory,
        content: t(translationKeys.storySpecialNotesContent),
      },
      {
        // buttons container
        target: inkindPage.payPal && selectors.donations,
        content: t(translationKeys.fundraisingContent),
        ...customSpotlightProps,
      },
      {
        // Calendar Add Request button, desktop
        target: inkindPage.isCalendar && isMd && selectors.addCalendarRequestDesktop,
        content: t(translationKeys.calendarContent),
      },
      {
        // Calendar Plus icon button, mobile
        target: inkindPage.isCalendar && !isMd && selectors.addCalendarRequestMobile,
        content: t(translationKeys.calendarContent),
      },
      {
        // Add Items button
        target: inkindPage.wishList && selectors.addItemsWishlist,
        content: t(translationKeys.wishlistContent),
      },
      {
        target: selectors.supportersButton,
        content: t(translationKeys.supportersContent),
      },
      {
        target: selectors.shareButton,
        content: t(translationKeys.sharingContent),
      },
    ];

    return steps
      .filter(step => step.target)
      .map(step => {
        // NOTE: offsets in <Joyride floaterProps={{... don't do anything, need to set it here
        // when offset is negative placement needs to be 'top' for tooltip to appear bellow the button
        step.offset = -2;
        step.placement = 'top';
        step.disableBeacon = true;
        step.target = `#${step.target}`; // NOTE: only id's can be used this way
        return step;
      });
  };

  const canEdit = canUserEditPage(inkindPage, userId);
  const hasUserSeenInkindTourForAnyPage = !!user?.welcomeScreenLastSeen;
  const hasUserSeenInkindTourForThisPage = userHasSeenInkindTourForThisPage(user, inkindPage);

  const isPageNew = isInkindPageNew(inkindPage);
  const showCancelButton = hasUserSeenInkindTourForAnyPage || true; // FIXME: remove || true after overlay is added

  useEffect(() => {
    if (!user || !inkindPage || !canEdit || hasLaunchedTour) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const shouldShowDialog =
      forceOpen ||
      urlParams.has('tour') ||
      (!hasUserSeenInkindTourForThisPage && (isPageNew || !hasUserSeenInkindTourForAnyPage));
    if (!shouldShowDialog) {
      return;
    }

    setTimeout(async () => {
      const handleNext = () => {
        setShowTour(true);
      };

      const handleCancel = async () => {
        showIntercomDefaultLauncher();

        await updateUserWelcomeScreenLastSeen(userId);
        await revalidateUser();
      };

      hideIntercomDefaultLauncher();
      openNewPageWelcomeModal(handleCancel, handleNext, showCancelButton);
      hasLaunchedTour = true;
    }, 1000);
  }, [
    inkindPage,
    canEdit,
    forceOpen,
    hasUserSeenInkindTourForThisPage,
    hasUserSeenInkindTourForAnyPage,
    isPageNew,
    showCancelButton,
    userId,
    user,
    revalidateUser,
  ]);

  const handleJoyride = (data: CallBackProps) => {
    const { status, lifecycle, step /*, type*/ } = data;

    // FIXME: this seems to fire twice
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      (async () => {
        await updateUserWelcomeScreenLastSeen(userId);
        await revalidateUser();

        Analytics.fireEvent(
          'InkindPageTourFinished',
          { status, inkindRouteId },
          () => ['status'],
          keys => keys.filter(key => key !== 'status')
        );

        showIntercomDefaultLauncher();
      })();
    }

    // fix for imperfect overlay positioning on first step
    // doesn't work in storybook, only in legacy app
    // select first step and it's tooltip lifecycle
    if (
      isBrowser() &&
      status === STATUS.RUNNING &&
      lifecycle === LIFECYCLE.TOOLTIP &&
      step.target === `#${selectors.pageSettings}`
    ) {
      // doesn't work without 500ms+ delay
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 500);
    }
  };

  // NOTE: 70px to compensate navbar height plus additional offset
  // maybe set different value for mobile and desktop
  const scrollOffset = 170;
  const steps = getSteps(inkindPage);

  return (
    <>
      {showTour && (
        <Joyride
          floaterProps={{
            hideArrow: false,
            offset: 200,
            options: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 200],
                  },
                },
              ],
            },
          }}
          run={true}
          showSkipButton={false}
          hideBackButton={false}
          tooltipComponent={props => <JoyrideTooltip {...props} showSkipButton={showCancelButton} />}
          spotlightPadding={0}
          steps={steps}
          callback={handleJoyride}
          scrollOffset={scrollOffset}
          disableOverlay={false}
          disableOverlayClose={true}
          styles={{ options: { zIndex: twConfig.theme.extend.zIndex.max } }}
        />
      )}
    </>
  );
}
